/**
 * Issuer API client for stablecoin invoicing (SPEC-IVC, ALL-5908).
 *
 * One function per operation the console pages need, across the seven route
 * families of `contracts/invoicing-issuer.openapi.yaml`. Every path here is the
 * contract's path verbatim and every request goes through `apiFetch` from
 * `src/lib/api.ts`, so cookie auth, CSRF, the correlation id and the 401/403
 * session probes stay in one place.
 *
 * ## Why this wraps `apiFetch` and not `fetchList` / `apiMutate`
 *
 * `src/lib/api.ts` is not edited (it is shared by every page). Its two list and
 * mutation helpers cannot express this contract, for reasons that are facts
 * about the contract rather than preferences:
 *
 *   - **Paging.** Every list route here returns `{items, nextCursor}`. `fetchList`
 *     reads `nextPageToken` / `hasMore` / `total` and returns none of them, so a
 *     caller could not fetch page 2 — the same defect recorded for the developer
 *     webhooks routes (gotcha 111). Rather than add fields to the gateway
 *     envelope to satisfy a client, this module reads `nextCursor` itself.
 *   - **Headers.** Every write requires an `Idempotency-Key`, and the draft
 *     patch plus the three resolution decisions require `If-Match`.
 *     `apiMutate` accepts `(path, method, body, scope)` and sends neither, so
 *     it cannot call a single mutating route in this contract.
 *   - **Refusal shape.** A refusal here is a `Problem` `{code, message,
 *     correlationId, details?}` (LLD section 12). The module reads `code` and
 *     keeps the support reference via `responseSupport`, which does understand
 *     both envelope shapes.
 *
 * ## Contract of every function in this module
 *
 *   - Never throws. A 4xx, a 5xx, a body that is not JSON and a transport
 *     failure all come back as the `{ok: false, error}` arm.
 *   - Never invents data. A 200 whose `items` is not an array is an error, not
 *     an empty page, and a 200 whose body cannot be read at all is an error,
 *     not a resource with null fields — an empty page or a null row is only
 *     ever evidence when the service said so (page-delivery-resources
 *     gotcha 36). A 204, the one bodyless success, is the exception.
 *   - Refusal codes are surfaced verbatim as bounded strings, for the caller to
 *     map through the LLD section 12 registry. Nobody is granted anything here:
 *     the service decides, this module only reports.
 */

import {
  apiFetch,
  boundedErrorText,
  idempotencyKey,
  responseSupport,
  type ApiErrorInfo,
} from "@/lib/api";
import type {
  CreateExportRequest,
  CreateInvoiceRequest,
  CreatePayerRequest,
  DecideResolutionRequest,
  Exception,
  ExceptionDetail,
  ExceptionListFilters,
  ExportJob,
  InvoicingSettings,
  InvoicingSettingsUpdate,
  Invoice,
  InvoiceListFilters,
  InvoiceSummary,
  IssueInvoiceResponse,
  JournalEntry,
  JournalListFilters,
  Payer,
  PayerListFilters,
  PayLinkReveal,
  Payment,
  PaymentListFilters,
  ProposeResolutionRequest,
  Refund,
  RefundAddressRequest,
  RefundListFilters,
  ReminderReceipt,
  RemindInvoiceRequest,
  Resolution,
  ResolutionDecision,
  UpdateInvoiceRequest,
  UpdatePayerRequest,
  VoidInvoiceRequest,
} from "./types";

/**
 * A refusal the client could not attribute to the service: the request never
 * completed (offline, DNS, a dropped connection, an aborted call). It is a
 * client-side code and is deliberately NOT one of the LLD section 12 registry
 * codes, so a caller can never mistake it for a service refusal. It matches the
 * code `use-hot-wallet-deposits.ts` already uses for the same situation.
 */
export const NETWORK_ERROR_CODE = "NETWORK_ERROR";

/**
 * A 2xx whose body is not the shape the contract promises. Distinct from an
 * empty page on purpose: a service that answers `200 {}` to a list route has
 * not told us there are no rows.
 */
export const MALFORMED_RESPONSE_CODE = "MALFORMED_RESPONSE";

/** The result of a single-resource call. */
export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiErrorInfo };

/** The result of a list call: a page of rows plus the cursor for the next one. */
export type ApiPage<T> =
  | { ok: true; items: T[]; nextCursor: string | null }
  | { ok: false; error: ApiErrorInfo };

export interface InvoicingRequestOptions {
  /** Aborts the call; the result then reports `NETWORK_ERROR`. */
  signal?: AbortSignal;
  /**
   * Reuse one key across retries of the *same* user action — that is what makes
   * a create safe to retry (the service replays the first result instead of
   * making a second invoice). Omitted, every call mints its own key, so two
   * separate actions are never deduplicated into one.
   */
  idempotencyKey?: string;
  /** Sent as `If-Match` when the contract requires or carries it. */
  ifMatch?: number | string;
}

/**
 * Options for a call whose `If-Match` the contract requires — the draft patch
 * and the two resolution decisions — so the version cannot be forgotten.
 */
export interface VersionedRequestOptions extends InvoicingRequestOptions {
  /** The row version the caller last read. A mismatch is 409 `VERSION_CONFLICT`. */
  ifMatch: number | string;
}

/* ------------------------------------------------------------ transport */

type QueryValue = string | number | boolean | undefined | null;

/** Builds the query string, dropping absent and empty values. */
function query(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

/**
 * Reads the refusal body. `code` is the contract's field; the two gateway
 * spellings are accepted so a proxy that rewrites the envelope still produces a
 * usable refusal rather than `HTTP_500`.
 */
function problemError(res: Response, data: unknown): ApiErrorInfo {
  const body = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  const reasonCode =
    boundedErrorText(body.code)
    ?? boundedErrorText(body.reason_code)
    ?? boundedErrorText(body.reasonCode)
    ?? `HTTP_${res.status}`;
  return { reasonCode, ...responseSupport(res, body) };
}

/**
 * Parses a response body without ever throwing. A refusal can arrive as an HTML
 * error page from the edge, and a 204 has no body at all; neither is a reason to
 * lose the status code we already have.
 */
async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}

interface Outcome {
  ok: boolean;
  status: number;
  data: unknown;
  error?: ApiErrorInfo;
}

async function call(
  path: string,
  init: RequestInit,
  scope: string,
  options: InvoicingRequestOptions,
): Promise<Outcome> {
  try {
    const res = await apiFetch(path, { ...init, signal: options.signal }, scope);
    const data = await readBody(res);
    if (!res.ok) return { ok: false, status: res.status, data, error: problemError(res, data) };
    return { ok: true, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null, error: { reasonCode: NETWORK_ERROR_CODE } };
  }
}

function unwrap<T>(outcome: Outcome): ApiResult<T> {
  if (!outcome.ok) {
    return {
      ok: false,
      error: outcome.error ?? { reasonCode: `HTTP_${outcome.status}` },
    };
  }
  // A 200 whose body could not be read is not a resource with null fields: the
  // service said OK and then sent something this client cannot use. Saying so
  // is better than handing a page `null`, which it would render as "not found".
  // 204 is the one status that legitimately carries no body.
  if (outcome.data === null && outcome.status !== 204) {
    return { ok: false, error: { reasonCode: MALFORMED_RESPONSE_CODE } };
  }
  return { ok: true, data: outcome.data as T };
}

function unwrapPage<T>(outcome: Outcome): ApiPage<T> {
  if (!outcome.ok) {
    return {
      ok: false,
      error: outcome.error ?? { reasonCode: `HTTP_${outcome.status}` },
    };
  }
  const body = (outcome.data && typeof outcome.data === "object" ? outcome.data : {}) as Record<
    string,
    unknown
  >;
  if (!Array.isArray(body.items)) {
    return { ok: false, error: { reasonCode: MALFORMED_RESPONSE_CODE } };
  }
  const cursor = body.nextCursor;
  return {
    ok: true,
    items: body.items as T[],
    nextCursor: typeof cursor === "string" && cursor.length > 0 ? cursor : null,
  };
}

/* --------------------------------------------------------------- verbs */

function get<T>(path: string, scope: string, options: InvoicingRequestOptions = {}): Promise<ApiResult<T>> {
  return call(path, { method: "GET" }, scope, options).then(unwrap<T>);
}

function getPage<T>(
  path: string,
  scope: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiPage<T>> {
  return call(path, { method: "GET" }, scope, options).then(unwrapPage<T>);
}

/**
 * The headers every write carries. `Idempotency-Key` is unconditional: the
 * contract requires it on all of these routes and the service refuses a replay
 * whose body differs, so a caller can never omit it by accident.
 */
function writeHeaders(
  scope: string,
  body: unknown,
  options: InvoicingRequestOptions,
): Headers {
  const headers = new Headers();
  if (body !== undefined) headers.set("Content-Type", "application/json");
  headers.set("Idempotency-Key", options.idempotencyKey ?? idempotencyKey(scope));
  if (options.ifMatch !== undefined) headers.set("If-Match", String(options.ifMatch));
  return headers;
}

function mutate<T>(
  path: string,
  method: string,
  body: unknown,
  scope: string,
  options: InvoicingRequestOptions,
): Promise<ApiResult<T>> {
  const init: RequestInit = { method, headers: writeHeaders(scope, body, options) };
  if (body !== undefined) init.body = JSON.stringify(body);
  return call(path, init, scope, options).then(unwrap<T>);
}

/* ------------------------------------------------------------- settings */

/** GET /v1/invoicing/settings — `getInvoicingSettings`. Requires `invoice:read`. */
export function getInvoicingSettings(
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<InvoicingSettings>> {
  return get<InvoicingSettings>("/v1/invoicing/settings", "invoicing_settings", options);
}

/**
 * PUT /v1/invoicing/settings — `updateInvoicingSettings`. Requires
 * `invoice:settings.manage`. `version` in the body is the optimistic-concurrency
 * token; a mismatch is 409 `VERSION_CONFLICT`. Enabling invoicing needs KYB
 * approval and a validated collection wallet per network.
 */
export function updateInvoicingSettings(
  body: InvoicingSettingsUpdate,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<InvoicingSettings>> {
  return mutate<InvoicingSettings>(
    "/v1/invoicing/settings",
    "PUT",
    body,
    "invoicing_settings_update",
    options,
  );
}

/* --------------------------------------------------------------- payers */

/** GET /v1/invoice-payers — `listInvoicePayers`. Requires `invoice:read`. */
export function listInvoicePayers(
  filters: PayerListFilters = {},
  options: InvoicingRequestOptions = {},
): Promise<ApiPage<Payer>> {
  const path = `/v1/invoice-payers${query({
    archived: filters.archived,
    cursor: filters.cursor,
    limit: filters.limit,
  })}`;
  return getPage<Payer>(path, "invoicing_payer_list", options);
}

/** POST /v1/invoice-payers — `createInvoicePayer`. Requires `invoice:payer.manage`. */
export function createInvoicePayer(
  body: CreatePayerRequest,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Payer>> {
  return mutate<Payer>("/v1/invoice-payers", "POST", body, "invoicing_payer_create", options);
}

/** GET /v1/invoice-payers/{payerId} — `getInvoicePayer`. Requires `invoice:read`. */
export function getInvoicePayer(
  payerId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Payer>> {
  return get<Payer>(
    `/v1/invoice-payers/${encodeURIComponent(payerId)}`,
    "invoicing_payer_detail",
    options,
  );
}

/**
 * PATCH /v1/invoice-payers/{payerId} — `updateInvoicePayer`. Requires
 * `invoice:payer.manage`. An update to an archived payer is refused with
 * `PAYER_ARCHIVED`; a stale version with `VERSION_CONFLICT`.
 */
export function updateInvoicePayer(
  payerId: string,
  body: UpdatePayerRequest,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Payer>> {
  return mutate<Payer>(
    `/v1/invoice-payers/${encodeURIComponent(payerId)}`,
    "PATCH",
    body,
    "invoicing_payer_update",
    options,
  );
}

/**
 * DELETE /v1/invoice-payers/{payerId} — `archiveInvoicePayer`. Requires
 * `invoice:payer.manage`. Soft delete: the row is archived, never removed, and a
 * new invoice for it is refused with `PAYER_ARCHIVED`.
 */
export function archiveInvoicePayer(
  payerId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Payer>> {
  return mutate<Payer>(
    `/v1/invoice-payers/${encodeURIComponent(payerId)}`,
    "DELETE",
    undefined,
    "invoicing_payer_archive",
    options,
  );
}

/* ------------------------------------------------------------- invoices */

/** GET /v1/invoices — `listInvoices`. Requires `invoice:read`. Newest first. */
export function listInvoices(
  filters: InvoiceListFilters = {},
  options: InvoicingRequestOptions = {},
): Promise<ApiPage<InvoiceSummary>> {
  const path = `/v1/invoices${query({
    document_status: filters.documentStatus,
    payment_status: filters.paymentStatus,
    payer: filters.payerId,
    due_from: filters.dueFrom,
    due_to: filters.dueTo,
    cursor: filters.cursor,
    limit: filters.limit,
  })}`;
  return getPage<InvoiceSummary>(path, "invoicing_invoice_list", options);
}

/** GET /v1/invoices/{invoiceId} — `getInvoice`. Requires `invoice:read`. */
export function getInvoice(
  invoiceId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Invoice>> {
  return get<Invoice>(`/v1/invoices/${encodeURIComponent(invoiceId)}`, "invoicing_invoice_detail", options);
}

/**
 * POST /v1/invoices — `createInvoiceDraft`. Requires `invoice:create`. Creates a
 * draft only: no number, address or pay link exists until issue. Refused with
 * `PAYER_ARCHIVED`, `ASSET_NOT_ENABLED` or `AMOUNT_PRECISION`.
 */
export function createInvoiceDraft(
  body: CreateInvoiceRequest,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Invoice>> {
  return mutate<Invoice>("/v1/invoices", "POST", body, "invoicing_invoice_create", options);
}

/**
 * PATCH /v1/invoices/{invoiceId} — `updateInvoiceDraft`. Requires
 * `invoice:create`. Draft only (`DOCUMENT_NOT_DRAFT`). `If-Match` is required by
 * the contract and carries the invoice version; a mismatch is 409
 * `VERSION_CONFLICT`.
 */
export function updateInvoiceDraft(
  invoiceId: string,
  body: UpdateInvoiceRequest,
  options: VersionedRequestOptions,
): Promise<ApiResult<Invoice>> {
  return mutate<Invoice>(
    `/v1/invoices/${encodeURIComponent(invoiceId)}`,
    "PATCH",
    body,
    "invoicing_invoice_update",
    options,
  );
}

/**
 * DELETE /v1/invoices/{invoiceId} — `deleteInvoiceDraft`. Requires
 * `invoice:create`. Draft only; an issued invoice can never be deleted.
 */
export function deleteInvoiceDraft(
  invoiceId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<null>> {
  return mutate<null>(
    `/v1/invoices/${encodeURIComponent(invoiceId)}`,
    "DELETE",
    undefined,
    "invoicing_invoice_delete",
    options,
  );
}

/**
 * POST /v1/invoices/{invoiceId}/issue — `issueInvoice`. Requires `invoice:issue`.
 * Freezes the document, assigns the number and mints the pay link. `payLink` is
 * returned exactly once, here; it is never readable again, never logged and
 * never put in a query string. Guards: `INVOICING_NOT_ENABLED`,
 * `KYB_NOT_APPROVED`, `COLLECTION_WALLET_MISSING`, `ASSET_DECIMALS_MISMATCH`,
 * `AMOUNT_PRECISION`, `DUE_DATE_PAST`, `DOCUMENT_NOT_DRAFT`, `VERSION_CONFLICT`.
 * The contract declares `If-Match` on this route but marks it optional; pass the
 * draft's version in `options.ifMatch` when the caller has read one.
 */
export function issueInvoice(
  invoiceId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<IssueInvoiceResponse>> {
  return mutate<IssueInvoiceResponse>(
    `/v1/invoices/${encodeURIComponent(invoiceId)}/issue`,
    "POST",
    undefined,
    "invoicing_invoice_issue",
    options,
  );
}

/**
 * POST /v1/invoices/{invoiceId}/void — `voidInvoice`. Requires `invoice:void`.
 * A reason is required and recorded. Refused with `PAYMENT_IN_FLIGHT` while any
 * payment is detected, final, held, cleared or applied, and with
 * `DOCUMENT_STATUS_INVALID` outside `issued`. The contract declares `If-Match`
 * on this route but marks it optional.
 */
export function voidInvoice(
  invoiceId: string,
  body: VoidInvoiceRequest,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Invoice>> {
  return mutate<Invoice>(
    `/v1/invoices/${encodeURIComponent(invoiceId)}/void`,
    "POST",
    body,
    "invoicing_invoice_void",
    options,
  );
}

/**
 * POST /v1/invoices/{invoiceId}/duplicate — `duplicateInvoice`. Requires
 * `invoice:create`. Copies lines, asset and due terms into a fresh draft; a
 * draft source is refused with `DUPLICATE_NOT_ALLOWED`.
 */
export function duplicateInvoice(
  invoiceId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Invoice>> {
  return mutate<Invoice>(
    `/v1/invoices/${encodeURIComponent(invoiceId)}/duplicate`,
    "POST",
    undefined,
    "invoicing_invoice_duplicate",
    options,
  );
}

/**
 * POST /v1/invoices/{invoiceId}/remind — `remindInvoice`. Requires
 * `invoice:remind`. The receipt proves the reminder was accepted for delivery,
 * not that it was delivered, and the pay link is never returned. Refused with
 * `DOCUMENT_STATUS_INVALID` or `NOTHING_DUE`.
 */
export function remindInvoice(
  invoiceId: string,
  body: RemindInvoiceRequest = {},
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<ReminderReceipt>> {
  return mutate<ReminderReceipt>(
    `/v1/invoices/${encodeURIComponent(invoiceId)}/remind`,
    "POST",
    body,
    "invoicing_invoice_remind",
    options,
  );
}

/**
 * POST /v1/invoices/{invoiceId}/pay-link/rotate — `rotatePayLink`. Requires
 * `invoice:issue`. Revokes the current link and mints a new one; the new link
 * text is returned exactly once and is never re-readable. A draft is refused
 * with `DOCUMENT_NOT_DRAFT`, a void or expired invoice with
 * `DOCUMENT_STATUS_INVALID`.
 */
export function rotatePayLink(
  invoiceId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<PayLinkReveal>> {
  return mutate<PayLinkReveal>(
    `/v1/invoices/${encodeURIComponent(invoiceId)}/pay-link/rotate`,
    "POST",
    undefined,
    "invoicing_pay_link_rotate",
    options,
  );
}

/** GET /v1/invoices/{invoiceId}/payments — `listInvoicePayments`. Requires `invoice:read`. */
export function listInvoicePayments(
  invoiceId: string,
  filters: PaymentListFilters = {},
  options: InvoicingRequestOptions = {},
): Promise<ApiPage<Payment>> {
  const path = `/v1/invoices/${encodeURIComponent(invoiceId)}/payments${query({
    state: filters.state,
    cursor: filters.cursor,
    limit: filters.limit,
  })}`;
  return getPage<Payment>(path, "invoicing_invoice_payments", options);
}

/**
 * GET /v1/invoices/{invoiceId}/journal — `getInvoiceJournal`. Requires
 * `invoice:read`. The read-only sub-ledger: the auditor's trail from the invoice
 * to each posting and transaction hash (IVC-150, IVC-151).
 */
export function getInvoiceJournal(
  invoiceId: string,
  filters: JournalListFilters = {},
  options: InvoicingRequestOptions = {},
): Promise<ApiPage<JournalEntry>> {
  const path = `/v1/invoices/${encodeURIComponent(invoiceId)}/journal${query({
    cursor: filters.cursor,
    limit: filters.limit,
  })}`;
  return getPage<JournalEntry>(path, "invoicing_invoice_journal", options);
}

/**
 * POST /v1/invoices/exports — `createInvoiceExport`. Requires `invoice:export`.
 * Returns the job immediately with status `QUEUED`; the file is produced
 * asynchronously and `downloadUrl` appears only once `COMPLETE`.
 */
export function createInvoiceExport(
  body: CreateExportRequest,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<ExportJob>> {
  return mutate<ExportJob>("/v1/invoices/exports", "POST", body, "invoicing_export_create", options);
}

/* ----------------------------------------------------------- exceptions */

/** GET /v1/invoice-exceptions — `listInvoiceExceptions`. Requires `invoice:read`. */
export function listInvoiceExceptions(
  filters: ExceptionListFilters = {},
  options: InvoicingRequestOptions = {},
): Promise<ApiPage<Exception>> {
  const path = `/v1/invoice-exceptions${query({
    state: filters.state,
    type: filters.type,
    invoice: filters.invoiceId,
    cursor: filters.cursor,
    limit: filters.limit,
  })}`;
  return getPage<Exception>(path, "invoicing_exception_list", options);
}

/**
 * GET /v1/invoice-exceptions/{exceptionId} — `getInvoiceException`. Requires
 * `invoice:read`. The exception with its payment and any resolution; a
 * cross-org id is a 404 with the same body a missing id gets.
 */
export function getInvoiceException(
  exceptionId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<ExceptionDetail>> {
  return get<ExceptionDetail>(
    `/v1/invoice-exceptions/${encodeURIComponent(exceptionId)}`,
    "invoicing_exception_detail",
    options,
  );
}

/* ---------------------------------------------------------- resolutions */

/**
 * POST /v1/invoice-resolutions — `proposeInvoiceResolution`. Requires
 * `invoice:resolve.propose`, or `invoice:refund.propose` for a `REFUND`. A
 * machine principal may propose; it may never approve. The proposal records the
 * proposer and moves no money. Refused with `RESOLUTION_ALREADY_OPEN`,
 * `APPLY_EXCEEDS_REMAINING` or `AMOUNT_PRECISION`.
 */
export function proposeInvoiceResolution(
  body: ProposeResolutionRequest,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Resolution>> {
  return mutate<Resolution>(
    "/v1/invoice-resolutions",
    "POST",
    body,
    "invoicing_resolution_propose",
    options,
  );
}

/**
 * POST /v1/invoice-resolutions/{resolutionId}/approve — `approveInvoiceResolution`.
 * Requires `invoice:resolve.approve`, or `invoice:refund.approve` for a `REFUND`.
 * Four-eyes, in this order: a machine principal is refused
 * (`MACHINE_PRINCIPAL_NOT_AN_APPROVER`), the approver must differ from the
 * proposer (`APPROVER_MUST_DIFFER_FROM_PROPOSER`), the permission is re-read,
 * fresh step-up is required (401 `AUTH_STEP_UP_REQUIRED`, with the challenge in
 * `details.challengeRef`), and the resolution must still be `PROPOSED`
 * (`RESOLUTION_NOT_PENDING` / `VERSION_CONFLICT`). `If-Match` carries the
 * proposal's version.
 */
export function approveInvoiceResolution(
  resolutionId: string,
  body: DecideResolutionRequest,
  options: VersionedRequestOptions,
): Promise<ApiResult<ResolutionDecision>> {
  return mutate<ResolutionDecision>(
    `/v1/invoice-resolutions/${encodeURIComponent(resolutionId)}/approve`,
    "POST",
    body,
    "invoicing_resolution_approve",
    options,
  );
}

/**
 * POST /v1/invoice-resolutions/{resolutionId}/reject — `rejectInvoiceResolution`.
 * Requires the **approve** permission (`invoice:refund.approve`, else
 * `invoice:resolve.approve`) and fresh step-up, and is human-only — the same
 * identity checks as approve, minus the proposer check, because a proposer
 * withdraws instead (see cancel). `reason` is required. `If-Match` carries the
 * proposal's version; a stale one is 409 `VERSION_CONFLICT`, a decided one 409
 * `RESOLUTION_NOT_PENDING`.
 */
export function rejectInvoiceResolution(
  resolutionId: string,
  body: DecideResolutionRequest,
  options: VersionedRequestOptions,
): Promise<ApiResult<ResolutionDecision>> {
  return mutate<ResolutionDecision>(
    `/v1/invoice-resolutions/${encodeURIComponent(resolutionId)}/reject`,
    "POST",
    body,
    "invoicing_resolution_reject",
    options,
  );
}

/**
 * POST /v1/invoice-resolutions/{resolutionId}/cancel — `cancelInvoiceResolution`.
 * Requires the propose permission. Only the proposer's principal id may cancel;
 * anyone else is refused with `FORBIDDEN`. The state becomes `CANCELLED`, not
 * `REJECTED`.
 */
export function cancelInvoiceResolution(
  resolutionId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Resolution>> {
  return mutate<Resolution>(
    `/v1/invoice-resolutions/${encodeURIComponent(resolutionId)}/cancel`,
    "POST",
    undefined,
    "invoicing_resolution_cancel",
    options,
  );
}

/* -------------------------------------------------------------- refunds */

/** GET /v1/invoice-refunds — `listInvoiceRefunds`. Requires `invoice:read`. */
export function listInvoiceRefunds(
  filters: RefundListFilters = {},
  options: InvoicingRequestOptions = {},
): Promise<ApiPage<Refund>> {
  const path = `/v1/invoice-refunds${query({
    state: filters.state,
    invoice: filters.invoiceId,
    cursor: filters.cursor,
    limit: filters.limit,
  })}`;
  return getPage<Refund>(path, "invoicing_refund_list", options);
}

/** GET /v1/invoice-refunds/{refundId} — `getInvoiceRefund`. Requires `invoice:read`. */
export function getInvoiceRefund(
  refundId: string,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Refund>> {
  return get<Refund>(
    `/v1/invoice-refunds/${encodeURIComponent(refundId)}`,
    "invoicing_refund_detail",
    options,
  );
}

/**
 * POST /v1/invoice-refunds/{refundId}/address — `setIssuerEnteredRefundAddress`.
 * Requires `invoice:refund.propose`. Allowed only on a refund in
 * `AWAITING_ADDRESS` (`REFUND_STATE_INVALID`), and the address must be a
 * network-correct, checksummed destination (`ADDRESS_INVALID`). The payer still
 * confirms with the one-time code, and the destination is screened before any
 * withdrawal; this call never moves funds.
 */
export function setIssuerEnteredRefundAddress(
  refundId: string,
  body: RefundAddressRequest,
  options: InvoicingRequestOptions = {},
): Promise<ApiResult<Refund>> {
  return mutate<Refund>(
    `/v1/invoice-refunds/${encodeURIComponent(refundId)}/address`,
    "POST",
    body,
    "invoicing_refund_address",
    options,
  );
}
